import { Component, type ErrorInfo, type ReactNode } from "react";

type ErrorBoundaryProps = {
  readonly children: ReactNode;
};

type ErrorBoundaryState = {
  readonly hasError: boolean;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Kalkonen.world failed to render", error, info);
  }

  public render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="relative grid min-h-screen place-items-center overflow-hidden bg-[radial-gradient(circle_at_70%_30%,rgba(217,255,82,.13),transparent_35%),#080908] p-6">
        <div
          className="pointer-events-none absolute inset-0 film-grain opacity-15"
          aria-hidden="true"
        />
        <section className="relative z-10 w-full max-w-[620px] border border-ink/15 bg-[#0d0f0c]/80 p-[clamp(32px,6vw,72px)] backdrop-blur-xl">
          <p className="mb-6 font-mono text-[10px] font-medium tracking-[.18em] text-accent uppercase">
            Something drifted off course
          </p>
          <h1 className="mb-5 text-[clamp(44px,7vw,76px)] leading-[.95] font-medium tracking-[-.06em]">
            Lost in the void.
          </h1>
          <p className="leading-[1.7] text-ink/60">
            The page hit an unexpected error. Reloading usually puts everything
            back in orbit.
          </p>
          <button
            className="mt-[18px] cursor-pointer border border-accent bg-accent px-[18px] py-[13px] font-mono text-[10px] font-medium tracking-[.12em] text-surface uppercase"
            type="button"
            onClick={() => window.location.reload()}
          >
            Reload experience
          </button>
        </section>
      </main>
    );
  }
}
